import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const usePortfolioChatQueries = () => {
  const api = useApi();

  return {
    listInfinite: (query?: ApiQueryParams<"getPortfolioChatsV2">) => {
      const limit = 50;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST(query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getPortfolioChatsV2({ ...query, Offset: pageParam * limit, Limit: limit })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 30 * 1000,
      });
    },
    list: (query?: ApiQueryParams<"getPortfolioChatsV2">) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST(query),
        queryFn: () => api.getPortfolioChatsV2(query),
        select: commonAPIDataSelector,
        staleTime: 30 * 1000,
      }),
    chatDetails: ({ chatId }: { chatId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_CHATS_DETAILS(chatId),
        queryFn: () => api.getPortfolioChatsDetailsV2(chatId),
        select: commonAPIDataSelector,
      }),
    privateChatDetails: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_USER_CHAT_DETAILS(userId),
        queryFn: () => api.getPortfolioChatsUserDetailsV2(userId),
        select: commonAPIDataSelector,
      }),
    listChatRepliesInfinite: ({ chatId }: { chatId: string }) => {
      const limit = 10;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_CHATS_REPLIES(chatId),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getPortfolioChatsRepliesV2(chatId, { Offset: pageParam * limit, Limit: limit })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    },
    listAvailableUsersInfinite: (query?: ApiQueryParams<"getPortfolioChatsAvailableUsersV2">) => {
      const limit = 20;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.PORTFOLIO_CHATS_AVAILABLE_USERS(query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getPortfolioChatsAvailableUsersV2({ ...query, Offset: pageParam * limit, Limit: limit })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 30 * 1000, // 30 seconds
      });
    },
  };
};
